import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

/**
 * AddressAutocomplete
 * ------------------------------------------------------------------
 * Free French address autocomplete backed by the government "Base
 * Adresse Nationale" (BAN) API. No API key required, generous
 * quotas. Falls back gracefully to a plain input if the API is
 * unreachable.
 *
 * Docs: https://adresse.data.gouv.fr/api-doc/adresse
 */
export function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,           // called with the full BAN feature { label, city, postcode, coords }
  placeholder = 'Commencez à taper votre adresse…',
  className = '',
  disabled = false,
  required = false,
  savedAddresses = null, // Array<{id, label, full_address, is_default}> to prepend as a "book" dropdown
  'data-testid': testId = 'address-autocomplete',
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const abortRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Keep local query in sync when the parent-controlled value changes
  useEffect(() => {
    if (value !== undefined && value !== query) setQuery(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced fetch to the BAN API
  useEffect(() => {
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setIsLoading(true);
        const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=6&autocomplete=1`;
        const res = await fetch(url, { signal: abortRef.current.signal });
        if (!res.ok) throw new Error('BAN error');
        const data = await res.json();
        const feats = Array.isArray(data?.features) ? data.features : [];
        setSuggestions(feats);
      } catch (e) {
        if (e?.name !== 'AbortError') setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Click-outside to close
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (feat) => {
    const label = feat?.properties?.label || '';
    setQuery(label);
    onChange?.(label);
    onSelect?.({
      label,
      city: feat?.properties?.city,
      postcode: feat?.properties?.postcode,
      housenumber: feat?.properties?.housenumber,
      street: feat?.properties?.street,
      context: feat?.properties?.context,
      coords: feat?.geometry?.coordinates, // [lng, lat]
    });
    setIsOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0) {
        e.preventDefault();
        pick(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showList = isOpen && (isLoading || suggestions.length > 0);

  const inputContent = useMemo(() => (
    <Input
      value={query}
      onChange={(e) => {
        const v = e.target.value;
        setQuery(v);
        onChange?.(v);
        setIsOpen(true);
        setHighlight(-1);
      }}
      onFocus={() => setIsOpen(true)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      className="pl-9"
      autoComplete="off"
      data-testid={testId}
    />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [query, disabled, placeholder, testId, suggestions, highlight, isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Saved-address quick picker for logged-in clients */}
      {Array.isArray(savedAddresses) && savedAddresses.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2" data-testid={`${testId}-saved-picker`}>
          <span className="text-xs text-gray-500 font-medium">Vos adresses :</span>
          {savedAddresses.map((a) => {
            const isActive = value && a.full_address && a.full_address.trim() === value.trim();
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  setQuery(a.full_address);
                  onChange?.(a.full_address);
                  onSelect?.({ label: a.full_address, city: a.city, postcode: a.postcode });
                  setIsOpen(false);
                }}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${isActive ? 'bg-primary text-primary-foreground border-primary' : 'bg-white border-gray-200 hover:border-primary hover:bg-primary/5'}`}
                data-testid={`${testId}-saved-${a.id}`}
              >
                <MapPin className="w-3 h-3" />
                <span className="font-medium">{a.label}</span>
                {a.is_default && !isActive && (
                  <span className="text-[9px] uppercase tracking-wider text-gray-400">défaut</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className="relative">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
        ) : (
          <MapPin className="w-4 h-4 text-gray-400" />
        )}
      </span>
      {inputContent}
      {showList && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg py-1"
          data-testid={`${testId}-list`}
        >
          {isLoading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Recherche…
            </li>
          )}
          {suggestions.map((feat, idx) => {
            const props = feat.properties || {};
            const active = idx === highlight;
            return (
              <li
                key={props.id || `${props.label}-${idx}`}
                role="option"
                aria-selected={active}
                onMouseDown={(e) => { e.preventDefault(); pick(feat); }}
                onMouseEnter={() => setHighlight(idx)}
                className={`px-3 py-2 cursor-pointer text-sm transition-colors ${active ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50'}`}
                data-testid={`${testId}-option-${idx}`}
              >
                <div className="font-medium leading-tight">{props.label}</div>
                {props.context && (
                  <div className="text-[11px] text-gray-500 leading-tight">
                    {props.context}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </div>
  );
}

export default AddressAutocomplete;
